import React, { useState } from "react";
import {
  useAddresses,
  useCreateAddress,
  useUpdateAddress,
  useDeleteAddress,
} from "../../hooks/useAddresses";

const AddressSection = () => {
  const {
    data: addresses,
    isLoading,
    isError,
    error,
    refetch,
  } = useAddresses();
  const createAddressMutation = useCreateAddress();
  const updateAddressMutation = useUpdateAddress();
  const deleteAddressMutation = useDeleteAddress();
  const [currentAddress, setCurrentAddress] = useState({
    id: null,
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "Brasil",
    number: "",
    neighborhood: "",
    reference: "",
    complement: "",
    principal: false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentAddress({
      ...currentAddress,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditing) {
        // Atualiza o endereço existente
        await updateAddressMutation.mutateAsync({
          id: currentAddress.id,
          data: currentAddress,
        });
      } else {
        // Cria um novo endereço
        await createAddressMutation.mutateAsync(currentAddress);
      }

      // Reset e fecha modal após sucesso
      resetForm();
      setIsModalOpen(false);
      refetch(); // Atualiza a lista de endereços
    } catch (err) {
      console.error("Erro ao salvar endereço:", err);
    }
  };

  const handleEdit = (address) => {
    setCurrentAddress({
      id: address.id || null,
      address: address.address || "",
      city: address.city || "",
      state: address.state || "",
      zipCode: address.zipCode || "",
      country: address.country || "Brasil",
      number: address.number || "",
      neighborhood: address.neighborhood || "",
      reference: address.reference || "",
      complement: address.complement || "",
      principal: address.principal || false,
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja remover este endereço?")) {
      try {
        await deleteAddressMutation.mutateAsync(id);
        refetch(); // Atualiza a lista de endereços
      } catch (err) {
        console.error("Erro ao remover endereço:", err);
      }
    }
  };

  const resetForm = () => {
    setCurrentAddress({
      id: null,
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "Brasil",
      number: "",
      neighborhood: "",
      reference: "",
      complement: "",
      principal: false,
    });
    setIsEditing(false);
  };

  if (isLoading) return <p>Carregando...</p>;
  if (isError) {
    console.error("Erro ao buscar endereços:", error);
    return <p>Erro ao carregar endereços.</p>;
  }

  return (
    <div className="space-y-4 pt-4 m-[2%]">
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Endereços</h2>
          <p className="text-sm text-gray-500">
            Gerencie seus endereços de entrega
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {addresses.map((address) => (
              <div
                key={address.id}
                className={`rounded-lg border bg-white shadow-sm ${
                  address.principal ? "border-2 border-green-500" : ""
                }`}>
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-medium">
                      {address.reference || "Endereço"}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(address)}
                        className="h-8 px-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(address.id)}
                        className="h-8 px-2 text-sm font-medium text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md">
                        Remover
                      </button>
                    </div>
                  </div>
                  {address.principal && (
                    <p className="text-sm text-green-600 font-medium">
                      Principal
                    </p>
                  )}
                </div>
                <div className="p-4 space-y-1">
                  <p className="text-sm">
                    {address.address}, {address.number}
                    {address.complement && `, ${address.complement}`}
                  </p>
                  <p className="text-sm">
                    {address.neighborhood}, {address.city} - {address.state}
                  </p>
                  <p className="text-sm">CEP: {address.zipCode}</p>
                  <p className="text-sm">{address.country}</p>
                  {address.reference && (
                    <p className="text-sm text-gray-500">
                      Referência: {address.reference}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
              Adicionar Novo Endereço
            </button>
          </div>
        </div>
      </div>

      {/* Modal para adicionar/editar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 ">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl h-[90%] overflow-auto">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">
                {isEditing ? "Editar Endereço" : "Adicionar Novo Endereço"}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço (Rua/Avenida)
                </label>
                <input
                  type="text"
                  name="address"
                  value={currentAddress.address}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={currentAddress.number}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    name="complement"
                    value={currentAddress.complement}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bairro
                </label>
                <input
                  type="text"
                  name="neighborhood"
                  value={currentAddress.neighborhood}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={currentAddress.city}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={currentAddress.state}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CEP
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={currentAddress.zipCode}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País
                </label>
                <input
                  type="text"
                  name="country"
                  value={currentAddress.country}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referência (opcional)
                </label>
                <input
                  type="text"
                  name="reference"
                  value={currentAddress.reference}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="principal"
                  checked={currentAddress.principal}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Definir como endereço principal
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  disabled={
                    createAddressMutation.isLoading ||
                    updateAddressMutation.isLoading
                  }>
                  {createAddressMutation.isLoading ||
                  updateAddressMutation.isLoading
                    ? "Salvando..."
                    : "Salvar Endereço"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressSection;
